import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, rupee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Account() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [units, setUnits] = useState([]);
  const [emis, setEmis] = useState([]);
  const [emiQr, setEmiQr] = useState(null);
  const [myReviews, setMyReviews] = useState([]);
  const loadEmi = () => api.get("/emi/mine").then((r) => setEmis(r.data)).catch(() => {});
  useEffect(() => {
    if (!loading && !user) nav("/login");
    if (user) {
      api.get("/orders/mine").then((r) => setOrders(r.data));
      api.get("/units/mine").then((r) => setUnits(r.data));
      api.get("/reviews/mine").then((r) => setMyReviews(r.data)).catch(() => {});
      loadEmi();
    }
  }, [user, loading]);

  const payNext = async (acc) => {
    try {
      const { data } = await api.get(`/payments/upi-qr?amount=${acc.monthly}&order_note=${encodeURIComponent("LEE BAG EMI " + acc.order_number)}`);
      setEmiQr({ acc, qr: data });
    } catch { toast.error("Could not load payment QR"); }
  };
  const confirmEmiPaid = async () => {
    try {
      const { data } = await api.post("/emi/pay-next", { account_id: emiQr.acc.id });
      setEmiQr(null);
      loadEmi();
      toast.success(data.status === "completed" ? "EMI fully paid. Thank you!" : "Installment recorded — verified by LEE BAG shortly");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadEpicture = async (o) => {
    try {
      const { data } = await api.get(`/epicture/${o.order_id}/download`);
      const a = document.createElement("a");
      a.href = data.image;
      a.download = `LEE-BAG-${(data.name || "epicture").replace(/\s+/g, "-")}.png`;
      a.click();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 fade-up">
      <div className="flex justify-between items-end mb-10">
        <div>
          <p className="eyebrow">My Account</p>
          <h1 className="font-serif text-3xl md:text-5xl mt-2">Welcome, {user.name}</h1>
          <p className="text-sm text-[#8C867D] mt-1">{user.email}</p>
        </div>
        <button onClick={async () => { await logout(); nav("/"); }} className="text-xs uppercase tracking-widest underline" data-testid="account-logout">Sign out</button>
      </div>

      {/* Profile summary */}
      <div className="grid grid-cols-3 gap-4 mb-12" data-testid="profile-stats">
        <div className="border border-[#E2DDD5] bg-white p-6 text-center">
          <p className="font-serif text-3xl">{orders.filter((o) => o.type !== "epicture").length}</p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8C867D] mt-1">Orders</p>
        </div>
        <div className="border border-[#E2DDD5] bg-white p-6 text-center">
          <p className="font-serif text-3xl">{emis.filter((a) => a.status === "active").reduce((s, a) => s + a.installments.filter((i) => !i.paid).length, 0)}</p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8C867D] mt-1">EMIs Pending</p>
        </div>
        <div className="border border-[#E2DDD5] bg-white p-6 text-center">
          <p className="font-serif text-3xl">{myReviews.length}</p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8C867D] mt-1">Reviews Given</p>
        </div>
      </div>

      <section className="mb-12">
        <h2 className="font-serif text-2xl mb-4">Order History</h2>
        {orders.length === 0 && <p className="text-sm text-[#8C867D]">No orders yet.</p>}
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="border border-[#E2DDD5] p-5 flex justify-between items-center" data-testid={`account-order-${o.order_id}`}>
              <div>
                <p className="font-mono text-sm">{o.order_id}</p>
                <p className="text-xs text-[#8C867D]">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} items</p>
                {o.delivery_date && <p className="text-xs text-[#997A31] mt-1">Expected delivery: {new Date(o.delivery_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>}
              </div>
              <div className="text-right">
                <p>{rupee(o.total)}</p>
                <p className="text-xs uppercase tracking-widest text-[#C5A059]">{o.status}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-serif text-2xl mb-4">My e-Pictures</h2>
        {orders.filter((o) => o.type === "epicture").length === 0 && <p className="text-sm text-[#8C867D]">No e-pictures purchased yet.</p>}
        <div className="space-y-3">
          {orders.filter((o) => o.type === "epicture").map((o) => (
            <div key={o.id} className="border border-[#E2DDD5] p-5 flex justify-between items-center" data-testid={`epicture-order-${o.order_id}`}>
              <div className="flex items-center gap-4">
                <img src={o.items?.[0]?.image} className="w-12 h-14 object-cover" alt="" />
                <div>
                  <p className="text-sm">{o.items?.[0]?.name}</p>
                  <p className="font-mono text-xs text-[#8C867D]">{o.order_id} · ₹1</p>
                </div>
              </div>
              {o.payment_status === "paid" ? (
                <button onClick={() => downloadEpicture(o)} className="btn-lux !py-2 !px-5" data-testid={`epicture-download-${o.order_id}`}>Download</button>
              ) : (
                <span className="text-xs uppercase tracking-widest text-[#C5A059]" data-testid={`epicture-pending-${o.order_id}`}>Unlocks after payment confirmation</span>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-serif text-2xl mb-4">My EMI Plans</h2>
        {emis.length === 0 && <p className="text-sm text-[#8C867D]">No EMI plans.</p>}
        <div className="space-y-4">
          {emis.map((a) => {
            const paid = a.installments.filter((i) => i.paid).length;
            const next = a.installments.find((i) => !i.paid);
            const overdue = next && next.overdue;
            return (
              <div key={a.id} className="border border-[#E2DDD5] p-5" data-testid={`emi-account-${a.order_number}`}>
                <div className="flex flex-wrap justify-between gap-4 items-center">
                  <div>
                    <p className="font-mono text-sm">{a.order_number}</p>
                    <p className="text-xs text-[#8C867D]">{paid}/{a.months} installments paid · {rupee(a.monthly)}/mo</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs uppercase tracking-widest ${a.status === "completed" ? "text-[#997A31]" : overdue ? "text-[#5B1C22]" : "text-[#C5A059]"}`} data-testid={`emi-status-${a.order_number}`}>
                      {a.status === "completed" ? "Completed" : overdue ? "Bill Pending — Overdue" : "Active"}
                    </span>
                    {a.status === "active" && next && (
                      <p className="text-xs text-[#8C867D] mt-1">Next due: {new Date(next.due_date).toLocaleDateString("en-IN")}</p>
                    )}
                  </div>
                </div>
                {a.status === "active" && (
                  <button onClick={() => payNext(a)} className="btn-lux mt-4 !py-2 !px-5" data-testid={`emi-pay-next-${a.order_number}`}>Pay Next Bill · {rupee(a.monthly)}</button>
                )}
                {a.status === "active" && (
                  <p className="text-[10px] text-[#8C867D] mt-2">New purchases stay locked until your EMI is cleared or LEE BAG enables them for you.</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {emiQr && (
        <div className="fixed inset-0 z-[100] bg-[#121212]/70 flex items-center justify-center p-4" onClick={() => setEmiQr(null)}>
          <div className="bg-white p-8 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()} data-testid="emi-pay-modal">
            <p className="eyebrow">EMI Installment</p>
            <p className="font-serif text-2xl mt-2">{rupee(emiQr.acc.monthly)}</p>
            <p className="text-xs text-[#8C867D] mt-1">Pay to <span className="font-mono">{emiQr.qr.upi_id}</span></p>
            <img src={emiQr.qr.qr_image} alt="EMI QR" className="w-48 h-48 mx-auto mt-4 border border-[#E2DDD5]" data-testid="emi-pay-qr" />
            <a href={emiQr.qr.upi_url} className="btn-lux-gold mt-4 inline-flex" data-testid="emi-pay-upi-button">Open UPI App & Pay</a>
            <button onClick={confirmEmiPaid} className="btn-lux w-full mt-4" data-testid="emi-pay-confirm">I have paid this installment</button>
            <button onClick={() => setEmiQr(null)} className="text-xs uppercase tracking-widest text-[#8C867D] mt-3">Cancel</button>
          </div>
        </div>
      )}

      <section className="mb-12">
        <h2 className="font-serif text-2xl mb-4">My Reviews</h2>
        {myReviews.length === 0 && <p className="text-sm text-[#8C867D]">You haven't reviewed any products yet.</p>}
        <div className="space-y-3">
          {myReviews.map((r) => (
            <div key={r.id} className="border border-[#E2DDD5] bg-white p-5" data-testid={`my-review-${r.id}`}>
              <div className="flex justify-between">
                <p className="text-sm font-medium">{r.product_name}</p>
                <span className="text-[#C5A059]">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
              </div>
              {r.text && <p className="text-sm text-[#55524D] mt-2">{r.text}</p>}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl mb-4">My LEE BAG Products</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {units.map((u) => (
            <div key={u.id} className="border border-[#E2DDD5] bg-white p-6 text-center" data-testid={`account-unit-${u.unit_id}`}>
              <img src={u.product_image} className="w-full h-48 object-cover mb-4" alt={u.product_name} />
              <p className="text-xs uppercase tracking-widest">{u.product_name}</p>
              <p className="font-mono text-[10px] text-[#8C867D] mt-1">Unit {u.unit_id}</p>
              <img src={u.qr_image} alt="QR" className="w-24 h-24 mx-auto mt-3" />
              <a href={u.verify_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-widest text-[#C5A059] mt-2 inline-block">View Certificate →</a>
            </div>
          ))}
          {units.length === 0 && <p className="text-sm text-[#8C867D] col-span-3">No authenticated pieces yet.</p>}
        </div>
      </section>
    </div>
  );
}
